import React from 'react';
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import styles from '../../../../index.module.scss';
import BaseModal from '../../../shared/components/base.modal';

type Key = string;

export interface EntityOption<T> {
  key: Key;
  label: string;
  raw: T;
}

interface EntityMultiSelectModalProps<T> {
  open: boolean;
  title: string;
  handleClose: () => void;

  // Items to select
  options: EntityOption<T>[];
  valueKeys: Key[];
  onChange: (keys: Key[]) => void;

  // Submit
  onSubmit: () => void;
  submitLabel?: string;
  disabled?: boolean;
  description?: string;

  // Empty state
  emptyText?: string;
  label?: string;
}

export default function EntityMultiSelectModal<T>({
                                                    open,
                                                    title,
                                                    handleClose,
                                                    options,
                                                    valueKeys,
                                                    onChange,
                                                    onSubmit,
                                                    submitLabel = 'Save',
                                                    disabled = false,
                                                    description,
                                                    emptyText = 'No items available',
                                                    label = title
                                                  }: EntityMultiSelectModalProps<T>) {
  return (
    <BaseModal open={open} handleClose={handleClose} width={600} minHeight={400}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 350 }}>
        <img alt="Logo" src="/logo120.svg" style={{ marginBottom: 8, alignSelf: 'center' }} />
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!disabled) onSubmit();
          }}
          autoComplete="off"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'space-between',
            gap: 3,
            minHeight: 220
          }}
        >
          {description && <Typography variant="body2">{description}</Typography>}
          <FormControl disabled={disabled} fullWidth sx={{ mt: 3, mb: 3 }}>
            <InputLabel id="entity-multiselect-label">{label}</InputLabel>
            <Select
              labelId="entity-multiselect-label"
              multiple
              value={valueKeys}
              onChange={(e) => onChange(e.target.value as string[])}
              input={<OutlinedInput id="select-multiple-chip" label={label} />}
              sx={{ height: 'auto !important' }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((k) => {
                    const opt = options.find((o) => o.key === k);
                    return <Chip key={k} label={opt?.label ?? k} sx={{ m: 0.5, maxWidth: 250 }} />;
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 48 * 4.5 + 8, width: 300 }
                }
              }}
            >
              {options.length ? (
                options.map((o) => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {emptyText}
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            disabled={disabled}
            type="submit"
            variant="contained"
            sx={{
              width: '100%',
              bgcolor: styles.mainBlue,
              minHeight: 48,
              fontWeight: 500,
              fontSize: 18,
              mt: 1
            }}
          >
            {submitLabel}
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
}
